/**
 * FAQSection Component
 * Landing page frequently asked questions with animated accordion
 */

import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

const FAQS = [
  {
    question: "What is MerchMidas and how does it work?",
    answer:
      "MerchMidas is an AI-powered SaaS platform that gives e-commerce brands six professional-grade visual tools in one place — virtual try-on, product staging, ad creative generation, gift personalisation, AR visualisation, and unboxing video creation. You upload your product images, choose a tool, and our Gemini-powered AI generates studio-quality visuals in seconds.",
  },
  {
    question: "Do I need any design or technical skills?",
    answer:
      "None at all. Every tool is built for non-designers. You point, click, describe what you want, and the AI handles the rest. Our guided workflows walk you through each step, so you go from raw product photo to polished visual without touching any design software.",
  },
  {
    question: "How does the credit system work?",
    answer:
      "Credits are consumed each time you generate an asset. Free accounts start with 100 credits per month. Pro accounts receive 1,000 credits, and Business accounts receive 5,000. Unused credits reset monthly. You can also purchase one-off credit packs at any time from your Billing page if you need a top-up.",
  },
  {
    question: "What subscription plans are available?",
    answer:
      "We offer four tiers: Free ($0 / 100 credits), Pro ($29/mo / 1,000 credits — no watermarks, priority processing), Business ($99/mo / 5,000 credits — API access, team seats, analytics), and Enterprise (custom pricing — white-label, dedicated support, unlimited usage). You can upgrade or downgrade at any time.",
  },
  {
    question: "Are the generated images watermarked?",
    answer:
      "Free plan outputs include a subtle MerchMidas watermark. Upgrading to Pro or higher removes watermarks entirely, giving you clean, production-ready assets you can publish or hand to clients straight away.",
  },
  {
    question: "Which AI model powers the generation?",
    answer:
      "All six tools are powered by Google's latest multimodal AI models via our secure cloud infrastructure, giving you state-of-the-art image understanding and generation. We continuously upgrade to newer model versions as they become available so your outputs keep improving automatically.",
  },
  {
    question: "Can I cancel my subscription at any time?",
    answer:
      "Yes — no lock-in, no cancellation fees. You can cancel directly from your Billing page and you'll retain full access until the end of your current billing period. We handle everything through Clerk's secure payment gateway, so your card details are never stored on our servers.",
  },
  {
    question: "Is my data safe? Who can see my product images?",
    answer:
      "Your images are processed securely and are never used to train AI models or shared with third parties. We use industry-standard encryption in transit and at rest. For Enterprise customers we offer dedicated data residency options and a signed Data Processing Agreement.",
  },
];

interface FAQItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
  index: number;
}

const FAQItem = ({ question, answer, isOpen, onToggle, index }: FAQItemProps) => (
  <div
    className={cn(
      "rounded-2xl glass border border-border/50 overflow-hidden transition-all duration-300",
      isOpen && "border-primary/40 shadow-lg shadow-primary/10"
    )}
    style={{ animationDelay: `${index * 60}ms` }}
  >
    <button
      className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left group"
      onClick={onToggle}
      aria-expanded={isOpen}
    >
      <span
        className={cn(
          "text-base font-semibold transition-colors duration-200",
          isOpen ? "text-primary" : "text-foreground group-hover:text-primary"
        )}
      >
        {question}
      </span>
      <span
        className={cn(
          "flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-all duration-300",
          isOpen
            ? "bg-primary/20 text-primary rotate-180"
            : "bg-border/30 text-foreground/60 group-hover:bg-primary/10 group-hover:text-primary"
        )}
      >
        <ChevronDown size={16} />
      </span>
    </button>

    <div
      className={cn(
        "overflow-hidden transition-all duration-300 ease-in-out",
        isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
      )}
    >
      <p className="px-6 pb-5 text-foreground/70 leading-relaxed text-sm">{answer}</p>
    </div>
  </div>
);

export const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggle = (index: number) =>
    setOpenIndex((prev) => (prev === index ? null : index));

  const half = Math.ceil(FAQS.length / 2);
  const leftColumn = FAQS.slice(0, half);
  const rightColumn = FAQS.slice(half);

  return (
    <section id="faq" className="py-20 px-6 relative overflow-hidden">
      {/* Subtle background glow */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/3 to-transparent pointer-events-none" />

      <div className="container mx-auto max-w-6xl relative z-10">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="inline-block text-xs font-semibold tracking-widest uppercase text-primary/70 mb-3 px-3 py-1 rounded-full border border-primary/20 bg-primary/5">
            Got Questions?
          </span>
          <h2 className="text-4xl font-bold text-primary mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-foreground/70 max-w-xl mx-auto">
            Everything you need to know about MerchMidas. Can't find your
            answer?{" "}
            <a
              href="mailto:support@echoversedb.org"
              className="text-primary hover:text-primary/80 underline underline-offset-2 transition-colors"
            >
              Drop us a line.
            </a>
          </p>
        </div>

        {/* Two-column accordion */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Left column */}
          <div className="flex flex-col gap-4">
            {leftColumn.map((faq, i) => (
              <FAQItem
                key={i}
                index={i}
                question={faq.question}
                answer={faq.answer}
                isOpen={openIndex === i}
                onToggle={() => toggle(i)}
              />
            ))}
          </div>

          {/* Right column */}
          <div className="flex flex-col gap-4">
            {rightColumn.map((faq, i) => {
              const globalIndex = half + i;
              return (
                <FAQItem
                  key={globalIndex}
                  index={globalIndex}
                  question={faq.question}
                  answer={faq.answer}
                  isOpen={openIndex === globalIndex}
                  onToggle={() => toggle(globalIndex)}
                />
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
